import type { ConnectError } from '@connectrpc/connect'
import {
  GenerationSalesMasterErrorReason,
  GenerationSalesMasterErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  type AppErrorInfoResolution,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'

/**
 * `GenerationSalesMasterErrorReason`
 * (occto-plan-master-reason-registry 設計書, backend `occtomaster` domain)
 * → i18n key + field routing registry for the GenerationSalesMaster
 * Create/Update modals (mirrors BgMaster/models/serverError.ts, decision
 * E). See that file's `BG_MASTER_REASON_KEYS` godoc for the `Record<Enum,
 * AppErrorInfoReasonEntry | undefined>` compile-time-coverage contract and
 * the `field` routing rationale (docs/adr/
 * structured-app-error-standard-errorinfo-envelope.md decision 2).
 *
 * D3/D4: `SUBMITTER_NOT_FOUND` (計画提出者 select) is the only
 * field-scoped entry (field=`submitterMasterId`); license mismatch /
 * member-BG / plant / duplicate are operation-scoped. D6: keys live
 * under this tab's own `serverError.reason.*` sub-namespace to
 * disambiguate reason names that also exist on the DSP tab (both have
 * `submitterNotFound` / `submitterLicenseMismatch`).
 *
 * `MEMBER_BG_LICENSE_MISMATCH` (gsp-member-bg-license-validation 設計書
 * decision 4): 所属BG license 不整合は単一入力欄に 1:1 帰属しないため
 * operation-scoped (既存の `submitterLicenseMismatch` /
 * `memberBgNotFound` と同型)。
 */
const GENERATION_SALES_MASTER_REASON_KEYS: Record<
  GenerationSalesMasterErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [GenerationSalesMasterErrorReason.UNSPECIFIED]: undefined,
  [GenerationSalesMasterErrorReason.SUBMITTER_NOT_FOUND]: {
    key: 'page:adminOcctoMaster.generationSalesPlanMaster.serverError.reason.submitterNotFound',
    field: 'submitterMasterId',
  },
  [GenerationSalesMasterErrorReason.SUBMITTER_LICENSE_MISMATCH]: {
    key: 'page:adminOcctoMaster.generationSalesPlanMaster.serverError.reason.submitterLicenseMismatch',
  },
  [GenerationSalesMasterErrorReason.MEMBER_BG_NOT_FOUND]: {
    key: 'page:adminOcctoMaster.generationSalesPlanMaster.serverError.reason.memberBgNotFound',
  },
  [GenerationSalesMasterErrorReason.PLANT_NOT_FOUND]: {
    key: 'page:adminOcctoMaster.generationSalesPlanMaster.serverError.reason.plantNotFound',
  },
  // AC-OMG-001b: reuses the pre-existing spec-worded duplicate key (the
  // dynamic `{{submitterCode}}` / `{{planCategoryLabel}}` interpolation
  // comes from the resolver's `values` argument, D1) rather than minting
  // a second copy of the same text under `serverError.reason.*`.
  [GenerationSalesMasterErrorReason.SUBMITTER_PLAN_CATEGORY_ALREADY_EXISTS]: {
    key: 'page:adminOcctoMaster.generationSalesPlanMaster.serverError.duplicateSubmitterPlan',
  },
  [GenerationSalesMasterErrorReason.MEMBER_BG_LICENSE_MISMATCH]: {
    key: 'page:adminOcctoMaster.generationSalesPlanMaster.serverError.reason.memberBgLicenseMismatch',
  },
}

/**
 * `resolveDetailedError` for GenerationSalesMaster's `useCrudModalForm`
 * (Create / Update). Resolves the `google.rpc.ErrorInfo` Connect error
 * detail, if any, against `GENERATION_SALES_MASTER_REASON_KEYS` via the
 * `GenerationSalesMasterErrorReasonSchema` reflection lookup. `values` is
 * passed through to `t(key, values)` for the duplicate reason's
 * `{{submitterCode}}` / `{{planCategoryLabel}}` interpolation (D1);
 * callers must source it from the fresh submitted form values (see
 * useCrudModalForm's resolveDetailedError contract, N3).
 */
export function resolveGenerationSalesMasterDetailedError(
  err: ConnectError,
  t: TFunction,
  values: Record<string, unknown>,
): AppErrorInfoResolution | undefined {
  return resolveAppErrorInfoMessage(
    err,
    GenerationSalesMasterErrorReasonSchema,
    GENERATION_SALES_MASTER_REASON_KEYS,
    t,
    values,
  )
}
